import { useState, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Grid,
  GridItem,
  AspectRatio,
  Icon,
  Button,
  Badge,
  List,
} from "@chakra-ui/react";
import { useColorModeValue } from "../ui/color-mode";
import { FaPlayCircle, FaBookOpen, FaLightbulb, FaCode, FaVolumeUp, FaStop } from "react-icons/fa";
import navigationSound from "../../assets/sounds/navigation.mp3";
import { useAccessibility } from "../../context/AccessibilityContext";
import { coursesData } from "../../data/courseData";
import { lessonContent } from "../../data/lessonContent";
import SonicCodeEngine from "../SonicCodeEngine";

/* ─── Sidebar Item ────────────────────────────────────────────── */
const SidebarItem = ({ item, isSelected, onSelect, isTextMode }) => {
  const listItemBg = useColorModeValue("gray.50", "gray.700");
  const selectedBg = useColorModeValue("blue.100", "blue.800");
  const hoverBg = useColorModeValue("gray.100", "gray.600");

  return (
    <Box
      as="button"
      onClick={() => onSelect(item)}
      width="100%"
      textAlign="left"
      bg={isSelected ? selectedBg : listItemBg}
      p={3}
      borderRadius="md"
      cursor="pointer"
      transition="background 0.2s, transform 0.2s"
      _hover={{ bg: isSelected ? selectedBg : hoverBg, transform: "scale(1.02)" }}
      role="option"
      aria-selected={isSelected}
    >
      <HStack spacing={3}>
        <Icon
          as={isTextMode ? FaBookOpen : FaPlayCircle}
          color="blue.500"
          w={5}
          h={5}
          aria-hidden="true"
        />
        <Text fontWeight="medium" fontSize="sm">{item.title}</Text>
      </HStack>
    </Box>
  );
};

/* ─── Text Lesson Panel ───────────────────────────────────────── */
const TextLessonPanel = ({ lesson, headingColor, textColor, cardBg, headingRef, onGenerateSummary, isSummarizing }) => {
  const codeBg = useColorModeValue("gray.900", "gray.950");
  const codeColor = useColorModeValue("green.300", "green.200");
  const tipBg = useColorModeValue("blue.50", "blue.900");
  const tipBorder = useColorModeValue("blue.300", "blue.500");
  const bulletColor = useColorModeValue("blue.500", "blue.300");

  const [isNarrating, setIsNarrating] = useState(false);

  useEffect(() => {
    // Stop any ongoing narration when component unmounts or lesson changes
    window.speechSynthesis.cancel();
    setIsNarrating(false);
    return () => window.speechSynthesis.cancel();
  }, [lesson.id]);

  const toggleNarration = () => {
    if (isNarrating) {
      window.speechSynthesis.cancel();
      setIsNarrating(false);
    } else {
      window.speechSynthesis.cancel(); // clear previous
      const fullText = `
        Lesson Title: ${lesson.title}. 
        Summary: ${lesson.summary}. 
        Key Concepts: ${lesson.keyPoints.map((p, i) => `Point ${i + 1}: ${p}`).join(". ")}.
        Pro Tip: ${lesson.tip}.
        After listening, you can explore the code example sonically below.
      `;
      const utterance = new SpeechSynthesisUtterance(fullText);
      utterance.rate = 1.0;
      utterance.onend = () => setIsNarrating(false);
      window.speechSynthesis.speak(utterance);
      setIsNarrating(true);
    }
  };

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={cardBg}
      borderRadius="xl"
      boxShadow="xl"
      role="article"
      aria-label={`Lesson: ${lesson.title}`}
    >
      <VStack spacing={7} align="stretch">

        {/* Title & Narration Button */}
        <HStack justify="space-between" wrap="wrap" gap={4}>
          <Heading
            as="h2"
            size="lg"
            color={headingColor}
            ref={headingRef}
            tabIndex={-1}
            aria-live="polite"
          >
            {lesson.title}
          </Heading>
          
          <HStack spacing={2} wrap="wrap">
            <Button
              colorScheme={isNarrating ? "red" : "blue"}
              variant={isNarrating ? "solid" : "outline"}
              leftIcon={<Icon as={isNarrating ? FaStop : FaVolumeUp} />}
              onClick={toggleNarration}
              aria-label={isNarrating ? "Stop Lesson Narration" : "Play Lesson as Audio Podcast"}
              size="md"
            >
              {isNarrating ? "Stop Narration" : "Play Lesson Audio"}
            </Button>
            {onGenerateSummary && (
              <Button
                onClick={onGenerateSummary}
                colorScheme={isSummarizing ? "red" : "purple"}
                variant={isSummarizing ? "solid" : "solid"}
                size="md"
                aria-label="Generate AI summary of the entire session"
              >
                {isSummarizing ? "⏹ Stop AI Recap" : "🧠 AI Session Recap"}
              </Button>
            )}
          </HStack>
        </HStack>

        {/* Summary */}
        <Text fontSize="md" color={textColor} lineHeight="tall">
          {lesson.summary}
        </Text>

        {/* Key Points */}
        <Box>
          <HStack mb={3} spacing={2}>
            <Icon as={FaBookOpen} color="blue.500" aria-hidden="true" />
            <Heading size="sm" color={headingColor}>Key Concepts</Heading>
          </HStack>
          <List.Root gap={2} pl={0}>
            {lesson.keyPoints.map((point, i) => (
              <List.Item
                key={i}
                display="flex"
                alignItems="flex-start"
                gap={2}
                pl={2}
              >
                <Text
                  as="span"
                  color={bulletColor}
                  fontWeight="bold"
                  minW="6"
                  aria-hidden="true"
                >
                  {i + 1}.
                </Text>
                <Text fontSize="sm" color={textColor} lineHeight="tall">
                  {point}
                </Text>
              </List.Item>
            ))}
          </List.Root>
        </Box>

        {/* Code Example */}
        <Box>
          <HStack mb={3} spacing={2}>
            <Icon as={FaCode} color="green.400" aria-hidden="true" />
            <Heading size="sm" color={headingColor}>Code Example</Heading>
            <Badge colorScheme="green" fontSize="xs">JavaScript</Badge>
          </HStack>
          <Box
            as="pre"
            bg={codeBg}
            color={codeColor}
            p={5}
            borderRadius="lg"
            overflowX="auto"
            fontSize="sm"
            fontFamily="'Courier New', Courier, monospace"
            lineHeight="1.7"
            tabIndex={0}
            aria-label={`Code example for ${lesson.title}`}
            role="region"
            whiteSpace="pre"
          >
            {lesson.codeExample}
          </Box>
          <Box mt={4}>
            <SonicCodeEngine initialCode={lesson.codeExample} readOnly={true} />
          </Box>
        </Box>

        {/* Pro Tip */}
        <Box
          p={4}
          bg={tipBg}
          borderLeft="4px solid"
          borderColor={tipBorder}
          borderRadius="md"
          role="note"
          aria-label="Pro tip"
        >
          <HStack spacing={2} mb={1}>
            <Icon as={FaLightbulb} color="yellow.400" aria-hidden="true" />
            <Text fontWeight="bold" fontSize="sm" color={headingColor}>
              Pro Tip
            </Text>
          </HStack>
          <Text fontSize="sm" color={textColor} lineHeight="tall">
            {lesson.tip}
          </Text>
        </Box>

      </VStack>
    </Box>
  );
};

/* ─── Course Screen ───────────────────────────────────────────── */
export const CourseScreen = ({ courseId = "python", onNext, onNavigateToAccessibility, onBack }) => {
  const courseData = coursesData[courseId] || coursesData["python"];
  const isTextMode = courseId === "javascript" || courseId === "react";

  // For text-based courses use lessonContent; for Python use videos
  const items = isTextMode
    ? (lessonContent[courseId] || [])
    : courseData.videos;

  const [selectedItem, setSelectedItem] = useState(items[0]);
  const { soundEnabled } = useAccessibility();
  const moduleListRef = useRef(null);
  const contentSectionRef = useRef(null);
  const contentHeadingRef = useRef(null);
  const accessibilityButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(null);
  const [isSummarizing, setIsSummarizing] = useState(false);

  const handleGenerateSessionSummary = () => {
    if (isSummarizing) {
       window.speechSynthesis.cancel();
       setIsSummarizing(false);
       return;
    }
    
    setIsSummarizing(true);
    let masterSummary = `Congratulations on finishing the ${courseData.title} learning session! Here is your AI generated session recap. `;
    
    items.forEach((item, index) => {
        masterSummary += `In module ${index + 1}, ${item.title}, `;
        if (item.keyPoints && item.keyPoints.length > 0) {
            // Include just the first main point to keep the recap concise
            masterSummary += `we learned that ${item.keyPoints[0].toLowerCase()}. `;
        }
    });
    
    masterSummary += "You are now fully prepared to tackle the quizzes. Have fun!";
    
    const utterance = new SpeechSynthesisUtterance(masterSummary);
    utterance.rate = 1.05;
    utterance.onend = () => setIsSummarizing(false);
    
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  const playSound = (sound) => {
    if (soundEnabled) new Audio(sound).play();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      contentSectionRef.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const currentIndex = items.findIndex((v) => v.id === selectedItem.id);
    let nextIndex;
    if (e.key === "ArrowDown") {
      nextIndex = currentIndex === items.length - 1 ? currentIndex : currentIndex + 1;
    } else {
      nextIndex = currentIndex === 0 ? currentIndex : currentIndex - 1;
    }
    setSelectedItem(items[nextIndex]);
  };

  const handleContentSectionKeyDown = (e) => {
    if (e.key === "ArrowLeft" && e.shiftKey) {
      e.preventDefault();
      moduleListRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedButtonIndex(0);
      accessibilityButtonRef.current?.focus();
    }
  };

  const handleButtonsKeyDown = (e) => {
    const currentIndex = focusedButtonIndex ?? 0;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      const next = currentIndex < 1 ? 1 : currentIndex;
      setFocusedButtonIndex(next);
      if (next === 0) accessibilityButtonRef.current?.focus();
      else nextButtonRef.current?.focus();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prev = currentIndex > 0 ? 0 : currentIndex;
      setFocusedButtonIndex(prev);
      if (prev === 0) accessibilityButtonRef.current?.focus();
      else nextButtonRef.current?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      contentHeadingRef.current?.focus();
    }
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.700", "white");
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box p={{ base: 2, md: 8 }} width="100%">
      <Grid
        templateColumns={{ base: "1fr", lg: "1fr 2.5fr" }}
        gap={8}
        width="100%"
        maxW="1600px"
        mx="auto"
      >
        {/* Sidebar */}
        <GridItem>
          <Box p={4} bg={cardBg} borderRadius="lg" boxShadow="xl" height="100%">
            <VStack
              ref={moduleListRef}
              spacing={3}
              align="stretch"
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="listbox"
              aria-label={`${courseData.title} ${isTextMode ? "lessons" : "videos"} playlist`}
            >
              <Heading size="md" mb={2} color={headingColor}>
                {courseData.title} Modules
              </Heading>

              {items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  isSelected={selectedItem.id === item.id}
                  onSelect={setSelectedItem}
                  isTextMode={isTextMode}
                />
              ))}
            </VStack>
          </Box>
        </GridItem>

        {/* Main content area */}
        <GridItem>
          <VStack
            spacing={6}
            align="stretch"
            ref={contentSectionRef}
            tabIndex={0}
            onKeyDown={handleContentSectionKeyDown}
            role="region"
            aria-label={isTextMode ? "Lesson content section" : "Video player section"}
          >
            {isTextMode ? (
              /* Text lesson panel */
              <TextLessonPanel
                lesson={selectedItem}
                headingColor={headingColor}
                textColor={textColor}
                cardBg={cardBg}
                headingRef={contentHeadingRef}
                onGenerateSummary={handleGenerateSessionSummary}
                isSummarizing={isSummarizing}
              />
            ) : (
              /* Video player (Python) */
              <Box p={6} bg={cardBg} borderRadius="lg" boxShadow="xl">
                <VStack spacing={5} align="stretch">
                  <AspectRatio ratio={16 / 9}>
                    <iframe
                      title={selectedItem.title}
                      src={`https://www.youtube.com/embed/${selectedItem.youtubeId}`}
                      allowFullScreen
                      style={{ borderRadius: "8px" }}
                    />
                  </AspectRatio>
                  <Heading
                    size="lg"
                    pt={2}
                    color={headingColor}
                    ref={contentHeadingRef}
                    tabIndex={-1}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowLeft" && e.shiftKey) {
                        e.preventDefault();
                        moduleListRef.current?.focus();
                      }
                    }}
                  >
                    {selectedItem.title}
                  </Heading>
                  <Text fontSize="md" color={textColor} minH="50px">
                    {selectedItem.description}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Action buttons */}
            <HStack
              justify="flex-end"
              role="group"
              aria-label="Course actions"
              onKeyDown={handleButtonsKeyDown}
              wrap="wrap"
              gap={3}
            >
              {onBack && (
                <Button
                  onClick={onBack}
                  size="lg"
                  variant="outline"
                  colorScheme="purple"
                  aria-label="Back to course selection"
                >
                  Back to Course Selection
                </Button>
              )}
              <Button
                ref={accessibilityButtonRef}
                onClick={onNavigateToAccessibility}
                size="lg"
                variant="outline"
                onFocus={() => setFocusedButtonIndex(0)}
                aria-label="Go to accessibility settings"
              >
                Accessibility
              </Button>
              <Button
                ref={nextButtonRef}
                onClick={() => { playSound(navigationSound); onNext(); }}
                colorScheme="blue"
                size="lg"
                onFocus={() => setFocusedButtonIndex(1)}
                aria-label="Continue to quiz selection"
              >
                Next to Quiz Selection
              </Button>
            </HStack>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};
